import { useRef, useState, type ChangeEvent } from "react";
import { cn } from "../lib/cn";
import { controlBase, controlOpen, controlSizes, type ControlSize } from "../lib/control";
import { useIsMobile } from "../lib/use-is-mobile";
import { BottomSheet } from "./bottom-sheet";
import { Button } from "./button";
import { useFieldControl } from "./field";
import { Popover } from "./popover";

export interface ColorPickerProps {
  value?: string; // Hex format e.g. #635BFF
  defaultValue?: string;
  onChange?: (value: string) => void;
  size?: ControlSize;
  disabled?: boolean;
  className?: string;
  id?: string;
  presets?: string[];
}

const DEFAULT_PRESETS = [
  "#635BFF", // Primary Accent
  "#3B82F6", // Blue / Info
  "#10B981", // Green / Success
  "#F59E0B", // Amber / Warning
  "#EF4444", // Red / Danger
  "#8B5CF6", // Purple
  "#EC4899", // Pink
  "#6B7280", // Neutral Muted
  "#111827", // Neutral Dark
  "#FFFFFF", // White
];

/**
 * Sélecteur de couleur 100% fait maison.
 * Remplace l'input HTML5 natif par un sélecteur riche, visuel avec palette et saisie directe.
 */
export function ColorPicker({
  value,
  defaultValue = "#635BFF",
  onChange,
  size = "md",
  disabled,
  className,
  id,
  presets = DEFAULT_PRESETS,
  ...props
}: ColorPickerProps) {
  const [internal, setInternal] = useState<string>(defaultValue);
  const isControlled = value !== undefined;
  const currentColor = isControlled ? value : internal;

  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const fieldProps = useFieldControl(props);
  const isMobile = useIsMobile();

  const selectColor = (hex: string) => {
    if (!isControlled) setInternal(hex);
    onChange?.(hex);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    selectColor(val);
  };

  const copyHex = () => {
    navigator.clipboard.writeText(currentColor);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const pickerContent = (
    <div>
      {/* Grand Aperçu dynamique */}
      <div
        className="relative flex h-24 w-full items-center justify-center rounded-lg border border-border shadow-inner"
        style={{ backgroundColor: currentColor }}
      >
        <span className="rounded-md bg-surface-raised/90 px-3 py-1 font-mono text-xs font-bold uppercase text-fg backdrop-blur-sm shadow-xs">
          {currentColor}
        </span>
      </div>

      {/* Palette de pastilles prédéfinies */}
      <div className="mt-3">
        <p className="mb-2 text-[11px] font-semibold uppercase text-fg-muted">
          Palette de marque
        </p>
        <div className="flex flex-wrap gap-2">
          {presets.map((hex) => (
            <button
              key={hex}
              type="button"
              onClick={() => selectColor(hex)}
              className={cn(
                "size-8 sm:size-7 rounded-full border border-border transform-gpu transition-transform duration-fast ease-standard active:scale-95 touch-manipulation",
                currentColor.toLowerCase() === hex.toLowerCase() &&
                  "scale-110 ring-2 ring-accent ring-offset-2 ring-offset-surface-raised",
              )}
              style={{ backgroundColor: hex }}
              aria-label={`Couleur ${hex}`}
            />
          ))}
        </div>
      </div>

      {/* Sélecteur natif HTML5 intégré discrètement pour choix précis */}
      <div className="mt-4 flex items-center gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            value={currentColor}
            onChange={handleInputChange}
            className="w-full rounded-md border border-border bg-surface-sunken px-3 py-2 font-mono text-sm uppercase font-semibold text-fg focus:border-accent focus:outline-none sm:py-1.5 sm:text-xs"
          />
        </div>
        <input
          type="color"
          value={currentColor}
          onChange={handleInputChange}
          className="size-9 cursor-pointer appearance-none rounded-md border border-border bg-transparent p-0 sm:size-8"
        />
        <Button size="xs" variant="outline" onClick={copyHex}>
          {copied ? "Copié !" : "Copier"}
        </Button>
      </div>
    </div>
  );

  return (
    <div ref={containerRef} className="relative inline-block w-full">
      <button
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        className={cn(
          controlBase,
          controlSizes[size],
          "flex cursor-pointer items-center justify-between gap-2.5 text-left touch-manipulation",
          isOpen && controlOpen,
          className,
        )}
        {...fieldProps}
      >
        <div className="flex items-center gap-2.5 truncate">
          <span
            className="size-5 shrink-0 rounded-full border border-border shadow-xs"
            style={{ backgroundColor: currentColor }}
          />
          <span className="font-mono text-xs font-semibold uppercase text-fg">
            {currentColor}
          </span>
        </div>
        <svg
          className="size-4 shrink-0 text-fg-subtle"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="10" cy="10" r="7" />
          <path d="M10 7v6M7 10h6" />
        </svg>
      </button>

      {/* Affichage Mobile : Vrai BottomSheet Vaul swipable */}
      {isMobile ? (
        <BottomSheet
          open={isOpen}
          onClose={() => setIsOpen(false)}
          title="Choisir une couleur"
        >
          {pickerContent}
        </BottomSheet>
      ) : (
        /* Affichage Desktop : panneau flottant portalisé dans la top layer */
        <Popover anchorRef={containerRef} open={isOpen} onClose={() => setIsOpen(false)} width={288}>
          <div className="p-4">{pickerContent}</div>
        </Popover>
      )}
    </div>
  );
}
